import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import Swal from 'sweetalert2';
import UserDetailModal from './UserDetailModal'; // UserDetailModal 임포트
import '../App.css'; // 공통 스타일 사용
import './UserManagementPageModals.css'; // 모달 전용 CSS
import './UserManagementPageButtons.css'; // Import the new CSS file

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
  const [isUserDetailEditMode, setIsUserDetailEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    name: '', // 이름 필드 추가
    position: '직원', // 직책 필드 추가, 기본값 '직원'
    role: 'USER',
    email: '',
    phoneNumber: '',
    notes: ''
  });

  const positions = ['대표', '전무', '이사', '부장', '과장', '대리', '직원']; // 직책 옵션

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire('오류', error.response?.data || '사용자 목록을 불러오는데 실패했습니다.', 'error');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'phoneNumber') {
      // 숫자만 남기고 모든 문자 제거
      let cleanedValue = value.replace(/[^0-9]/g, '');
      let formattedNumber = '';

      if (cleanedValue.startsWith('02')) { // 서울 지역번호 (2자리)
        if (cleanedValue.length <= 2) {
          formattedNumber = cleanedValue;
        } else if (cleanedValue.length <= 6) {
          formattedNumber = `${cleanedValue.substring(0, 2)}-${cleanedValue.substring(2)}`;
        } else if (cleanedValue.length <= 10) {
          formattedNumber = `${cleanedValue.substring(0, 2)}-${cleanedValue.substring(2, 6)}-${cleanedValue.substring(6)}`;
        } else {
          formattedNumber = `${cleanedValue.substring(0, 2)}-${cleanedValue.substring(2, 6)}-${cleanedValue.substring(6, 10)}`;
        }
      } else if (cleanedValue.startsWith('0') && cleanedValue.length >= 3) { // 일반 휴대폰 번호 (3자리 시작)
        if (cleanedValue.length <= 3) {
          formattedNumber = cleanedValue;
        } else if (cleanedValue.length <= 7) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3)}`;
        } else if (cleanedValue.length <= 11) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7)}`;
        } else {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7, 11)}`;
        }
      } else { // 기타 (지역번호 3자리 등)
        if (cleanedValue.length <= 3) {
          formattedNumber = cleanedValue;
        } else if (cleanedValue.length <= 7) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3)}`;
        } else if (cleanedValue.length <= 11) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7)}`;
        } else {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7, 11)}`;
        }
      }
      formattedValue = formattedNumber;
    }

    setNewUserData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleCreateUser = async () => {
    try {
      if (!newUserData.username || !newUserData.password || !newUserData.name || !newUserData.position || !newUserData.role) {
        Swal.fire('경고', '모든 필드를 입력해주세요.', 'warning');
        return;
      }

      // 비밀번호 (생년월일) 유효성 검사 (6자리 숫자)
      if (!/^\d{6}$/.test(newUserData.password)) {
        Swal.fire('경고', '비밀번호는 생년월일 6자리 숫자 형식이어야 합니다.', 'warning');
        return;
      }

      const response = await axiosInstance.post('/admin/users', newUserData);
      Swal.fire('성공', `사용자 ${response.data.name}(${response.data.username})이(가) 성공적으로 생성되었습니다.`, 'success');
      setIsCreateUserModalOpen(false);
      setNewUserData({ username: '', password: '', name: '', position: '직원', role: 'USER', email: '', phoneNumber: '', notes: '' }); // 초기화
      fetchUsers(); // 사용자 목록 새로고침
    } catch (error) {
      console.error('Error creating user:', error);
      Swal.fire('오류', error.response?.data || '사용자 생성 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const result = await Swal.fire({
      title: `정말로 사용자 ${username}을(를) 삭제하시겠습니까?`,
      text: "이 사용자를 삭제하면 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/admin/users/${userId}`);
        Swal.fire(
          '삭제됨!',
          `사용자 ${username}이(가) 성공적으로 삭제되었습니다.`, 
          'success'
        );
        fetchUsers(); // 사용자 목록 새로고침
      } catch (error) {
        console.error('Error deleting user:', error);
        Swal.fire(
          '오류!',
          error.response?.data || '사용자 삭제 중 오류가 발생했습니다.',
          'error'
        );
      }
    }
  };

  const openUserDetailModal = (user, isEditMode = false) => {
    setSelectedUser(user);
    setIsUserDetailEditMode(isEditMode);
    setIsUserDetailModalOpen(true);
  };

  const closeUserDetailModal = () => {
    setSelectedUser(null);
    setIsUserDetailEditMode(false);
    setIsUserDetailModalOpen(false);
  };

  const handleUpdateUser = async (updatedUserData) => {
    try {
      await axiosInstance.put(`/admin/users/${updatedUserData.id}`, updatedUserData);
      Swal.fire('성공', '사용자 정보가 성공적으로 수정되었습니다.', 'success');
      closeUserDetailModal();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      Swal.fire('오류', error.response?.data || '사용자 정보 수정 중 오류가 발생했습니다.', 'error');
    }
  };

  const filteredUsers = users.filter(user =>
    String(user.username).toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(user.name).toLowerCase().includes(searchTerm.toLowerCase()) || // 이름으로 검색 추가
    String(user.position).toLowerCase().includes(searchTerm.toLowerCase()) || // 직책으로 검색 추가
    String(user.role).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email && String(user.email).toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.phoneNumber && String(user.phoneNumber).toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.notes && String(user.notes).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="container">
      <br/>
      <h2>사용자 관리</h2>
      <br/><br/><br/>
      <div className="page-actions">
        <input
          type="text"
          placeholder="사용자 검색..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="button-group">
          <button onClick={() => setIsCreateUserModalOpen(true)} className="register-button">
            새 사용자 등록
          </button>
        </div>
      </div>
      <br/><br/><br/>
      {filteredUsers.length > 0 ? (
        <table className="main-table">
          <thead>
            <tr><th style={{ width: '15%' }}>사용자 이름 (ID)</th><th style={{ width: '15%' }}>직책</th> {/* 직책 컬럼 추가 */}<th style={{ width: '10%' }}>권한</th><th style={{ width: '20%' }}>이메일</th><th style={{ width: '20%' }}>전화번호</th><th style={{ width: '20%' }}>관리</th></tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
            <tr key={user.id} onClick={() => openUserDetailModal(user, false)} style={{ cursor: 'pointer' }}>
              <td>{user.username}</td>
              <td>{user.position}</td> {/* 직책 표시 */}
              <td>{user.role === "ADMIN" ? "관리자" : user.role === "USER" ? "유저" : user.role === "MIDADMIN" ? "중간관리자" : "게스트"}</td>
              <td>{user.email}</td>
              <td>{user.phoneNumber}</td>
              <td>
                <div className="user-actions-container"> {/* New container for buttons */}
                  <button onClick={(e) => { e.stopPropagation(); openUserDetailModal(user, true); }} className="user-edit-button">수정</button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.id, user.username); }} className="user-delete-button">삭제</button>
                </div>
              </td>
            </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>등록된 사용자가 없습니다.</p>
      )}

      {/* 사용자 생성 모달 */}
      {isCreateUserModalOpen && (
        <div className="modal-overlay" onClick={() => setIsCreateUserModalOpen(false)}>
          <div className="modal-content user-management-modal" onClick={(e) => e.stopPropagation()}>
            <h3>새 사용자 계정 생성</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }} className="form-grid">
              <div className="form-group">
                <label>사용자 이름 (ID):</label>
                <input
                  type="text"
                  name="username"
                  value={newUserData.username}
                  onChange={handleUserInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>이름:</label> {/* 이름 입력 필드 */}
                <input
                  type="text"
                  name="name"
                  value={newUserData.name}
                  onChange={handleUserInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>직책:</label> {/* 직책 드롭다운 */}
                <select name="position" value={newUserData.position} onChange={handleUserInputChange} required>
                  {positions.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>비밀번호:</label>
                <input
                  type="text" // type을 text로 변경
                  name="password"
                  value={newUserData.password}
                  onChange={handleUserInputChange}
                  placeholder="생년월일 6자리 (예: 900101)" // placeholder 추가
                  required
                />
              </div>
              <div className="form-group">
                <label>권한:</label>
                <select name="role" value={newUserData.role} onChange={handleUserInputChange}>
                  <option value="USER">USER</option>
                  <option value="GUEST">GUEST</option>
                  <option value="MIDADMIN">MIDADMIN</option>
                  <option value="ADMIN">ADMIN</option>
                </select>
              </div>
              <div className="form-group">
                <label>이메일:</label>
                <input
                  type="email"
                  name="email"
                  value={newUserData.email}
                  onChange={handleUserInputChange}
                />
              </div>
              <div className="form-group">
                <label>전화번호:</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={newUserData.phoneNumber}
                  onChange={handleUserInputChange}
                />
              </div>
              <div className="form-group notes-full-width">
                <label>비고:</label>
                <textarea
                  name="notes"
                  value={newUserData.notes}
                  onChange={handleUserInputChange}
                ></textarea>
              </div>
              <div className="modal-actions">
                <button type="submit" className="action-button">생성</button>
                <button type="button" onClick={() => setIsCreateUserModalOpen(false)} className="action-button cancel-button">취소</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 사용자 수정/상세 모달 */}
      {isUserDetailModalOpen && (
        <UserDetailModal
          isOpen={isUserDetailModalOpen}
          onClose={closeUserDetailModal}
          onUpdateUser={handleUpdateUser}
          userData={selectedUser}
          isEditMode={isUserDetailEditMode}
        />
      )}
    </div>
  );
}

export default UserManagementPage;
